import { useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useAuth } from "../../auth";

const TOKEN_KEY = "ub_auth_token";
const CLIENT_TMA_URL = ((import.meta.env.VITE_CLIENT_TMA_URL as string | undefined) || "https://uniquest-tma.pages.dev").replace(/\/$/, "");

const wrapStyle: CSSProperties = {
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  gap: 10,
  minHeight: "calc(100dvh - 120px)",
  padding: 12,
};

const noteStyle: CSSProperties = {
  border: "1px solid var(--surface-3)",
  borderRadius: 12,
  padding: "10px 12px",
  background: "var(--surface-1)",
  color: "var(--text-2)",
  fontSize: 13,
  lineHeight: 1.45,
};

const frameStyle: CSSProperties = {
  width: "100%",
  minHeight: 720,
  height: "calc(100dvh - 178px)",
  border: "1px solid var(--surface-3)",
  borderRadius: 14,
  background: "#050706",
};

export function DcClientImpersonationSection(): React.ReactElement {
  const { user } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) || "" : "";
  const src = useMemo(() => {
    const url = new URL(CLIENT_TMA_URL + "/");
    // MVP fallback: token in URL. The iframe also supports postMessage below.
    if (token) url.searchParams.set("admin_token", token);
    return url.toString();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const targetOrigin = new URL(CLIENT_TMA_URL).origin;
    const send = (): void => iframeRef.current?.contentWindow?.postMessage({ type: "ub-admin-token", token }, targetOrigin);
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== targetOrigin) return;
      if ((event.data as { type?: string } | null)?.type === "ub-need-admin-token") send();
    };
    window.addEventListener("message", onMessage);
    const timer = window.setTimeout(send, 700);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
    };
  }, [token]);

  if (!token) {
    return (
      <section style={wrapStyle}>
        <div style={noteStyle}>
          <strong>Личина клиента недоступна</strong><br />
          Нужен staff-token вход. Выйди и зайди через обычную staff-авторизацию, чтобы iframe мог передать серверу `staff-admin`.
        </div>
      </section>
    );
  }

  return (
    <section style={wrapStyle}>
      <div style={noteStyle}>
        <strong>Личина клиента</strong><br />
        Внутри открыт клиентский TMA в режиме админ-просмотрщика. Доступ подтверждает сервер по staff-token и `ADMIN_TG_IDS`; выбранная личина работает read-only.
        {user?.telegramId ? <span> Текущий TG staff: {user.telegramId}.</span> : null}
      </div>
      <iframe
        ref={iframeRef}
        title="Личина клиента"
        src={src}
        style={frameStyle}
        sandbox="allow-scripts allow-same-origin allow-forms"
        allow="clipboard-read; clipboard-write"
      />
    </section>
  );
}
